import { useEffect, useRef, useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext.tsx";
import { SERA_PHONE } from "@/components/atomic-crm/misc/constants";
import { SSOAuthButton } from "./SSOAuthButton";

const SeraWordmark = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 9,
        background: "#3ECF8E",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow:
          "0 0 0 1px rgba(62,207,142,0.4), 0 4px 16px rgba(62,207,142,0.25)",
      }}
    >
      <svg width="14" height="16" viewBox="0 0 100 115" fill="none">
        <path
          d="M62 38C62 38 36 36 36 47C36 55 62 55 62 65C62 76 36 76 36 76"
          stroke="#000"
          strokeWidth="16"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
    <div>
      <p
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: "#F0F0F8",
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
        }}
      >
        Sera
      </p>
      <p
        style={{
          fontSize: 10,
          fontWeight: 500,
          color: "rgba(255,255,255,0.3)",
          letterSpacing: "0.03em",
          lineHeight: 1,
          marginTop: 1,
        }}
      >
        AI Receptionist
      </p>
    </div>
  </div>
);

export const LoginPage = (props: { redirectTo?: string }) => {
  const { googleWorkplaceDomain, disableEmailPasswordAuthentication } =
    useConfigurationContext();
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";
    if (!shouldNotify || hasDisplayedRecoveryNotification.current) return;
    hasDisplayedRecoveryNotification.current = true;
    notify("crm.auth.recovery_email_sent", {
      type: "success",
      messageArgs: {
        _: "If you're a registered user, you should receive a password recovery email shortly.",
      },
    });
    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => setLoading(false))
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  void translate;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        fontFamily: "Satoshi, Inter, sans-serif",
        background: "#080810",
      }}
    >
      {/* Left panel */}
      <div
        style={{
          display: "none",
          position: "relative",
          width: "52%",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "48px 56px",
          background: "#0A0A14",
          borderRight: "1px solid rgba(255,255,255,0.05)",
          overflow: "hidden",
        }}
        className="lg:flex"
      >
        {/* Subtle radial glow */}
        <div
          style={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(62,207,142,0.06) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative" }}>
          <SeraWordmark />
        </div>

        <div style={{ position: "relative" }}>
          <h2
            style={{
              fontFamily: "Cormorant Garamond, Georgia, serif",
              fontSize: 52,
              fontWeight: 500,
              color: "#F0F0F8",
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              marginBottom: 20,
            }}
          >
            Your front desk,
            <br />
            <span style={{ color: "#3ECF8E" }}>always on.</span>
          </h2>
          <p
            style={{
              fontSize: 15,
              color: "rgba(255,255,255,0.38)",
              lineHeight: 1.7,
              maxWidth: 380,
            }}
          >
            Sera answers every call, books appointments, and logs everything —
            24/7, without missing a beat.
          </p>

          {/* Stats */}
          <div style={{ display: "flex", gap: 0, marginTop: 48 }}>
            {[
              { value: "24/7", label: "Always on" },
              { value: "< 2s", label: "Answer time" },
              { value: "90%", label: "Cost savings" },
            ].map((stat, i) => (
              <div
                key={stat.label}
                style={{
                  flex: 1,
                  paddingRight: 32,
                  borderRight:
                    i < 2 ? "1px solid rgba(255,255,255,0.07)" : "none",
                  paddingLeft: i > 0 ? 32 : 0,
                }}
              >
                <p
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: "#F0F0F8",
                    letterSpacing: "-0.02em",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {stat.value}
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.3)",
                    marginTop: 4,
                    fontWeight: 500,
                  }}
                >
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Demo line */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: "#3ECF8E",
                opacity: 0.4,
                animation: "ping 1.5s cubic-bezier(0,0,0.2,1) infinite",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: "#3ECF8E",
              }}
            />
          </div>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.3)" }}>
            Try the demo:{" "}
            <span style={{ color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>
              {SERA_PHONE}
            </span>
          </p>
        </div>
      </div>

      {/* Right panel — login form */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 32px",
        }}
      >
        <div style={{ width: "100%", maxWidth: 360 }}>
          {/* Mobile wordmark */}
          <div
            className="lg:hidden"
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 40,
            }}
          >
            <SeraWordmark />
          </div>

          {/* Heading */}
          <div style={{ marginBottom: 36 }}>
            <h1
              style={{
                fontFamily: "Cormorant Garamond, Georgia, serif",
                fontSize: 34,
                fontWeight: 500,
                color: "#F0F0F8",
                letterSpacing: "-0.01em",
                lineHeight: 1.1,
                marginBottom: 8,
              }}
            >
              Welcome back.
            </h1>
            <p
              style={{
                fontSize: 14,
                color: "rgba(255,255,255,0.35)",
                fontWeight: 400,
              }}
            >
              Sign in to your Sera dashboard
            </p>
          </div>

          {/* Form */}
          {!disableEmailPasswordAuthentication && (
            <Form
              style={{ display: "flex", flexDirection: "column", gap: 0 }}
              onSubmit={handleSubmit}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  marginBottom: 20,
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.3)",
                      marginBottom: 8,
                    }}
                  >
                    Email
                  </label>
                  <TextInput
                    label=""
                    source="email"
                    type="email"
                    validate={required()}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.3)",
                      marginBottom: 8,
                    }}
                  >
                    Password
                  </label>
                  <TextInput
                    label=""
                    source="password"
                    type="password"
                    validate={required()}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 font-semibold text-[14px] rounded-xl cursor-pointer border-0"
                disabled={loading}
                style={{
                  background: "#3ECF8E",
                  color: "#000",
                  letterSpacing: "0.01em",
                  boxShadow: loading
                    ? "none"
                    : "0 0 0 1px rgba(62,207,142,0.3), 0 4px 16px rgba(62,207,142,0.2)",
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? (
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <svg
                      className="animate-spin w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="3"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Signing in…
                  </span>
                ) : (
                  "Sign in"
                )}
              </Button>

              <div style={{ textAlign: "center", marginTop: 16 }}>
                <Link
                  to="/forgot-password"
                  style={{
                    fontSize: 13,
                    color: "rgba(255,255,255,0.3)",
                    textDecoration: "none",
                    fontWeight: 400,
                  }}
                >
                  Forgot your password?
                </Link>
              </div>
            </Form>
          )}

          {googleWorkplaceDomain && (
            <div style={{ marginTop: 16 }}>
              <SSOAuthButton className="w-full" domain={googleWorkplaceDomain}>
                Sign in with Google Workspace
              </SSOAuthButton>
            </div>
          )}
        </div>
      </div>

      <Notification />
      <style>{`@keyframes ping { 75%, 100% { transform: scale(2); opacity: 0; } }`}</style>
    </div>
  );
};
